import { api } from '@/shared/lib/api';
import type { ProjectSortByField, SortOrder } from '@/shared/lib/types';

export async function fetchProject(projectId: string) {
  return api.getProject(projectId);
}

export async function fetchProjects(
  sessionId: string,
  params?: { pageNumber?: number; pageSize?: number; sortBy?: ProjectSortByField | null; sortOrder?: SortOrder | null },
) {
  return api.getProjects(sessionId, params);
}

export async function createProject(name: string) {
  return api.createProject(name);
}

export async function updateProject(projectId: string, name: string) {
  return api.updateProject(projectId, { name });
}

export async function deleteProject(projectId: string) {
  return api.deleteProject(projectId);
}

export async function fetchProjectQuestionnaires(projectId: string) {
  return api.getQuestionnaires(projectId);
}

export async function deleteProjectQuestionnaire(questionnaireId: string) {
  return api.deleteQuestionnaire(questionnaireId);
}

export async function runProjectQuestionnaire(questionnaireId: string) {
  return api.runQuestionnaire(questionnaireId);
}

export async function retryFailedProjectQuestionnaire(questionnaireId: string) {
  return api.retryFailedQuestionnaireAnswers(questionnaireId);
}

export async function calculateProjectQuestionnaireConfidence(questionnaireId: string) {
  return api.calculateQuestionnaireConfidence(questionnaireId);
}

export async function calculateProjectQuestionConfidence(questionnaireId: string, questionId: string) {
  return api.calculateQuestionConfidence(questionnaireId, questionId);
}

export async function runAllProjectQuestionnaires(projectId: string) {
  return api.runProjectQuestionnaires(projectId);
}

export async function cancelProjectQuestionnaireRun(projectId: string) {
  return api.cancelQuestionnaireRun(projectId);
}

export function getProjectReportUrl(projectId: string) {
  return api.getProjectReportUrl(projectId);
}

export function getQuestionnaireReportUrl(questionnaireId: string) {
  return api.getQuestionnaireReportUrl(questionnaireId);
}

export async function fetchProjectReportUrls(projectId: string): Promise<string[]> {
  const endpoint = api.getProjectReportUrl(projectId);
  const response = await fetch(endpoint, {
    headers: { Accept: 'application/json' },
  });

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return [];
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload: unknown = await response.json();
  const urls = Array.isArray(payload)
    ? payload
    : (typeof payload === 'object' && payload != null && Array.isArray((payload as { urls?: unknown }).urls)
        ? (payload as { urls: unknown[] }).urls
        : []);

  return urls.filter((u): u is string => typeof u === 'string' && u.length > 0);
}
